/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
    CSSProperties,
    Dispatch,
    forwardRef,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    CellData,
    flexRender,
    Row,
    RowData,
    SortingState,
    TableFeatures,
    TableOptions,
    Updater,
    useTable,
} from '@tanstack/react-table';
import { ColumnDef, Features, features } from './tableFeatures';
import { checkMatchByLowercase } from '../../../services/helpers/columnFilter';
import { TableRowContent } from '../../../types/Layout/TableRowContent';
import TableHeaderTypes from '../../../types/tableHeaderType';
import MenuLineIcon from 'remixicon-react/MenuLineIcon';
import FullscreenLineIcon from 'remixicon-react/FullscreenLineIcon';
import FullscreenExitLineIcon from 'remixicon-react/FullscreenExitLineIcon';
import styled from 'styled-components';
import * as skin from './Table.skin';
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon';
import SearchFilterMenu from '../../molecules/SearchFilterMenu/SearchFilterMenu';
import SidebarMenu from '../Menu/SidebarMenu';
import ArrowLeftSLineIcon from 'remixicon-react/ArrowLeftSLineIcon';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import CheckBox from '../Checkbox/Checkbox';
import Pagination from '../../../types/table/Pagination';
import AddNewRow from '../../../types/table/AddNewRow';
import RowStyles from './RowStyles';
import Sort from './Sort';
import Flex from '../Box/Flex';
import PerPageSelector from '../PerPageSelector/PerPageSelector';

interface TableProps<T extends TableRowContent> {
    data: Array<T>;
    columns: Array<ColumnDef<T>>;
    savedColumns?: string[];
    allColumns?: Array<ColumnDef<T>>;
    pagination?: Pagination;
    sort?: Sort<T>;
    onRowClicked?: (row: T, newTab?: boolean) => void;
    onSelectedRowsChange?: (rows: T[]) => void;
    skipPageReset?: boolean;
    stateFulData?: boolean;
    disableTable?: boolean;
    selectableProperty?: (original: T) => string | undefined;
    getRowStyles?: (row: T) => RowStyles;
    columnMenu?: boolean;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    expandable?: Expandable<T>;
    newRowProperties?: AddNewRow;
    setColumns?: Dispatch<SetStateAction<ColumnDef<T>[]>>;
    defaultColumns?: string[];
    tableHeaderType?: TableHeaderTypes;
    initialCheckAll?: boolean;
    uncheckAllDelegate?: React.Dispatch<SetStateAction<() => void>>;
}

type Expandable<T> = {
    expandableContent: (row: T) => JSX.Element;
};

declare module '@tanstack/table-core' {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    interface ColumnMeta<
        TFeatures extends TableFeatures,
        TData extends RowData,
        TValue extends CellData = CellData
    > {
        /* eslint-enable @typescript-eslint/no-unused-vars */
        style?: {
            textAlign?: string;
            width?: string;
            rowColor?: string;
        };
        sortKey?: string;
        hideHeader?: boolean;
        csv?: {
            label: string;
            accessor: (row: TData, ...args: any[]) => string;
        };
    }
}

const useCombinedRefs = (...refs: any[]): React.MutableRefObject<any> => {
    const targetRef = useRef();

    useEffect(() => {
        refs.forEach(ref => {
            if (!ref) return;

            if (typeof ref === 'function') {
                ref(targetRef.current);
            } else {
                // eslint-disable-next-line no-param-reassign
                ref.current = targetRef.current;
            }
        });
    }, [refs]);

    return targetRef;
};

const IndeterminateCheckbox = forwardRef<HTMLInputElement, any>(
    ({ indeterminate, ...rest }: any, ref: React.Ref<HTMLInputElement>) => {
        const defaultRef = useRef(null) as React.MutableRefObject<any>;
        const resolvedRef = useCombinedRefs(ref, defaultRef);

        useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CheckBox dataTestId="activate-checkbox" setRef={resolvedRef} {...rest} />;
    }
);

function Table<T extends TableRowContent>({
    data,
    sort,
    columns,
    savedColumns,
    stateFulData,
    pagination,
    onRowClicked,
    onSelectedRowsChange,
    newRowProperties,
    expandable,
    selectableProperty,
    disableTable,
    columnMenu,
    isExpanded,
    onExpandToggle,
    setColumns,
    allColumns,
    defaultColumns,
    tableHeaderType,
    initialCheckAll,
    uncheckAllDelegate,
    getRowStyles,
}: TableProps<T>) {
    const getColumsToUse: () => (ColumnDef<T> | undefined)[] = () => {
        const savedTableColumns =
            savedColumns &&
            allColumns &&
            savedColumns.map(x =>
                allColumns.find(y => y.header === x || checkMatchByLowercase(y.header as string, x))
            );

        const columnsToUse =
            savedTableColumns && savedTableColumns.length !== 0 && columns === savedTableColumns
                ? savedTableColumns
                : columns;

        return columnsToUse;
    };

    const getColumsUseCallback = useCallback(getColumsToUse, [columns]);

    const [toggleSortMenu, setToggleSortMenu] = useState(false);
    const [statefulTableColumns] = useState(getColumsUseCallback());

    const tableData = useMemo(() => data, [data]);
    const tableColumns = useMemo(() => getColumsUseCallback(), [getColumsUseCallback]);
    const [sortId, direction] = sort?.initialSortbyState?.split('-') || ['', ''];

    const id: string = useMemo(
        () => tableColumns.find(c => c && c.meta?.sortKey === sortId)?.id ?? sortId,
        [tableColumns]
    );

    const initialSortState = [{ id, desc: direction === 'desc' }];
    const [sorting, setSorting] = useState<SortingState>(initialSortState);
    const [shouldSort, setShouldSort] = useState(false);

    const injectSorting = (options: TableOptions<Features, T>): TableOptions<Features, T> => {
        return sort
            ? {
                  ...options,
                  state: { sorting, ...options.state },
                  onSortingChange: (sortingState: Updater<SortingState>) => {
                      setSorting(sortingState);
                      setShouldSort(true);
                  },
              }
            : options;
    };

    const opt = injectSorting({
        features,
        data: tableData as any,
        sortDescFirst: false,
        columns: tableColumns as ColumnDef<T>[],
        manualSorting: sort ? true : false,
    });
    const table = useTable(opt);

    const sortArrowStyle = (sortedDesc: boolean | undefined): CSSProperties => {
        return {
            transform: sortedDesc ? 'rotate(-360deg)' : 'rotate(180deg)',
            marginTop: sortedDesc ? '0' : '-0.3rem',
        };
    };

    const onMiddleMouseButtonClick = (event: any, row: any) => {
        if (event.button === 1 && onRowClicked) onRowClicked(row, true);
    };

    const checkRowClickable = (e: { target: HTMLInputElement }) => {
        if (e.target.tagName !== 'INPUT') return true;
        return false;
    };

    const rowClickRedirect = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: any) => {
        if ((e.metaKey || e.altKey || e.ctrlKey) && onRowClicked) {
            onRowClicked(row, true);
            return;
        }

        if (
            checkRowClickable(e as unknown as { target: HTMLInputElement }) &&
            onRowClicked !== undefined
        )
            onRowClicked(row);
    };

    const nextPage = () => {
        table.toggleAllRowsSelected(false);
        if (pagination) pagination.nextPage();
    };

    const previousPage = () => {
        table.toggleAllRowsSelected(false);
        if (pagination) pagination.previousPage();
    };

    useEffect(() => {
        table.toggleAllRowsSelected(initialCheckAll ?? false);

        if (uncheckAllDelegate) uncheckAllDelegate(() => () => table.toggleAllRowsSelected(false));
    }, [table.toggleAllRowsSelected, uncheckAllDelegate]);

    useEffect(() => {
        if (onSelectedRowsChange) {
            onSelectedRowsChange(
                table.getSelectedRowModel().flatRows.map((r: { original: T }) => {
                    return r.original;
                })
            );
        }
    }, [onSelectedRowsChange, table.getSelectedRowModel().flatRows]);

    useEffect(() => {
        const getSort = () => {
            if (sorting.length === 0) return sorting;

            const [firstSort] = sorting;
            const { meta: { sortKey = '' } = {} } =
                tableColumns.find(x => x?.id == firstSort.id) || {};

            if (sortKey.length > 0) return [{ id: sortKey, desc: firstSort.desc }];

            return sorting;
        };

        if (sort && shouldSort) {
            Promise.resolve().then(() => {
                sort.sortFunction(getSort());
                setShouldSort(false);
            });
        }
    }, [sorting, shouldSort]);

    interface Props {
        position: string;
    }

    const ActionButtons = () => (
        <>
            {onExpandToggle && columnMenu && (
                <TableActionButton onClick={() => setToggleSortMenu(true)} title="Choose columns">
                    <StyledMenuLineIcon size="17" />
                </TableActionButton>
            )}
            {onExpandToggle && (
                <TableActionButton
                    onClick={onExpandToggle}
                    title={isExpanded ? 'Collapse table' : 'Expand table'}
                >
                    {isExpanded ? (
                        <FullscreenExitLineIcon size="17" />
                    ) : (
                        <FullscreenLineIcon size="17" />
                    )}
                </TableActionButton>
            )}
        </>
    );

    const hasPaginationControls = !!pagination && !!data && data.length > 0;
    const hasUpperBar = hasPaginationControls || !!onExpandToggle;

    const ActionOnlyBar = () => (
        <PaginationStyle>
            <ActionBar>
                <ActionButtonGroup>
                    <ActionButtons />
                </ActionButtonGroup>
            </ActionBar>
        </PaginationStyle>
    );

    const PaginationComponent = ({ position }: Props) => {
        if (!pagination || !data || data.length <= 0) {
            return position === 'upper' && onExpandToggle ? <ActionOnlyBar /> : null;
        }

        const p = pagination;
        const showActionButtons = position === 'upper' && onExpandToggle;

        const paginationControls = (
            <PaginationControls>
                {p.metaData?.range && (
                    <Results>
                        {p.metaData?.range.from}-{p.metaData?.range.to} of{' '}
                        {new Intl.NumberFormat().format(p.metaData?.totalNumberOfRecords)}
                    </Results>
                )}
                <PrevButton
                    data-testid={position + '-table-prev-page-button'}
                    onClick={() => previousPage()}
                    disabled={p.metaData.numberOfPages === 1 || !p.links?.prev}
                >
                    <ArrowLeftSLineIcon size="17" />
                </PrevButton>
                <NextButton
                    data-testid={position + '-table-next-page-button'}
                    onClick={() => nextPage()}
                    disabled={p.metaData.numberOfPages === 1 || !p.links?.next}
                >
                    <ArrowRightSLineIcon size="17" />
                </NextButton>
            </PaginationControls>
        );

        return (
            <PaginationStyle>
                {p.perPage && (
                    <PerPageWrapper>
                        <PerPageSelector value={p.perPage.value} onChange={p.perPage.onChange} />
                    </PerPageWrapper>
                )}
                {showActionButtons ? (
                    <PageControlsStack>
                        <ActionButtonGroup>
                            <ActionButtons />
                        </ActionButtonGroup>
                        {paginationControls}
                    </PageControlsStack>
                ) : (
                    paginationControls
                )}
            </PaginationStyle>
        );
    };

    return (
        <>
            {toggleSortMenu &&
                createPortal(
                    <SidebarMenu isVisible={toggleSortMenu} toggle={() => setToggleSortMenu(false)}>
                        <SearchFilterMenu
                            close={() => setToggleSortMenu(false)}
                            columns={
                                (stateFulData
                                    ? statefulTableColumns
                                    : tableColumns) as ColumnDef<T>[]
                            }
                            allColumns={allColumns ?? columns}
                            setColumns={setColumns}
                            defaultColumns={defaultColumns ?? []}
                            tableHeaderType={tableHeaderType}
                        />
                    </SidebarMenu>,
                    document.body
                )}
            <TableArea>
                <PaginationComponent position="upper" />
                <TableWrapper>
                    <Container disabled={disableTable}>
                        <THead hasPagination={hasUpperBar}>
                            {table.getHeaderGroups().map((headerGroup, headerIndex) => {
                                const rowKey = `tr${headerIndex}`;
                                return (
                                    <Tr key={rowKey}>
                                        {onSelectedRowsChange && (
                                            <Th>
                                                <IndeterminateCheckbox
                                                    {...{
                                                        checked: table.getIsAllRowsSelected(),
                                                        indeterminate:
                                                            table.getIsSomeRowsSelected(),
                                                        onChange:
                                                            table.getToggleAllRowsSelectedHandler(),
                                                    }}
                                                />
                                            </Th>
                                        )}
                                        {headerGroup.headers.map((header, i) => {
                                            const { column } = header;
                                            const key = `header-${column.id}-${i}`;
                                            const isSortable = column.getCanSort();

                                            return (
                                                <React.Fragment key={key}>
                                                    {!column.columnDef.meta?.hideHeader && (
                                                        <Th
                                                            isSortable={isSortable}
                                                            style={
                                                                column.columnDef.meta
                                                                    ?.style as CSSProperties
                                                            }
                                                        >
                                                            <Flex
                                                                data-testid="sort-header"
                                                                onClick={
                                                                    column.id !== 'selection' &&
                                                                    isSortable
                                                                        ? column.getToggleSortingHandler()
                                                                        : () => {}
                                                                }
                                                                style={{
                                                                    justifyContent:
                                                                        column.columnDef.meta?.style
                                                                            ?.textAlign,
                                                                }}
                                                            >
                                                                {flexRender(
                                                                    column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                                <span>
                                                                    {column.getIsSorted() ? (
                                                                        <SortArrow
                                                                            style={sortArrowStyle(
                                                                                column.getIsSorted() ===
                                                                                    'desc'
                                                                            )}
                                                                        >
                                                                            <ArrowDownSLineIcon size="17" />
                                                                        </SortArrow>
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                </span>
                                                            </Flex>
                                                        </Th>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                        {!onExpandToggle && columnMenu && (
                                            <ThIcon onClick={() => setToggleSortMenu(true)}>
                                                <StyledMenuLineIcon size="17" />
                                            </ThIcon>
                                        )}
                                    </Tr>
                                );
                            })}
                        </THead>

                        <tbody>
                            {table
                                .getRowModel()
                                .rows.map((row: Row<Features, T>, rowIndex: number) => {
                                    const expandableKey = `expandable-${rowIndex}`;

                                    return (
                                        <React.Fragment key={expandableKey}>
                                            <Tr
                                                data-testid={`table-row-${rowIndex}`}
                                                isRowClickable={!!onRowClicked}
                                                onClick={e => rowClickRedirect(e, row.original)}
                                                onMouseDown={e =>
                                                    onMiddleMouseButtonClick(e, row.original)
                                                }
                                                isCrossedOver={row.original.isCrossedOver}
                                                isModified={
                                                    row.original.isChanged || row.original.isAdded
                                                }
                                            >
                                                {(onSelectedRowsChange || selectableProperty) && (
                                                    <Td onClick={e => e.stopPropagation()}>
                                                        <IndeterminateCheckbox
                                                            id={
                                                                selectableProperty
                                                                    ? selectableProperty(
                                                                          row.original
                                                                      )
                                                                    : undefined
                                                            }
                                                            checked={row.getIsSelected()}
                                                            indeterminate={row.getIsSomeSelected()}
                                                            onChange={row.getToggleSelectedHandler()}
                                                        />
                                                    </Td>
                                                )}

                                                {row.getVisibleCells().map((cell, i) => {
                                                    const cellKey = `cell${i}`;

                                                    return (
                                                        <Td
                                                            style={{
                                                                ...(cell.column.columnDef.meta
                                                                    ?.style as CSSProperties),
                                                                ...(getRowStyles &&
                                                                    getRowStyles(row.original)),
                                                            }}
                                                            key={cellKey}
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </Td>
                                                    );
                                                })}

                                                {!onExpandToggle && columnMenu && <Td />}
                                            </Tr>
                                            {row.original.isExpanded && (
                                                <Tr key={expandableKey} isExpanded>
                                                    <Td colSpan={row.getVisibleCells().length}>
                                                        {expandable?.expandableContent(
                                                            row.original
                                                        )}
                                                    </Td>
                                                </Tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                            {newRowProperties !== undefined && newRowProperties.newRowAdded && (
                                <Tr isExpanded>
                                    <Td
                                        colSpan={
                                            table.getRowModel().rows.length > 0
                                                ? table.getRowModel().rows[0].getAllCells().length
                                                : undefined
                                        }
                                    >
                                        {newRowProperties.newRowContent}
                                    </Td>
                                </Tr>
                            )}
                        </tbody>
                    </Container>
                </TableWrapper>
            </TableArea>
            <PaginationComponent position="lower" />
        </>
    );
}

export default Table;

const SortArrow = styled.div`
    width: 100%;
`;

interface TableSkinProps {
    disabled?: boolean;
}

const TableArea = styled.div`
    position: relative;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    width: 100%;
`;

const Container = styled.table<TableSkinProps>`
    ${props => skin.Container()}
    ${props =>
        props.disabled &&
        `
        opacity: 0.5;
        pointer-events: none;
    `}
`;

const THead = styled.thead<{ hasPagination?: boolean }>`
    background-color: ${props => props.theme.colors.subtle.light};

    ${props =>
        !props.hasPagination &&
        `
        & th:first-child {
            border-top-left-radius: 0.5rem;
        }
        & th:last-child {
            border-top-right-radius: 0.5rem;
        }
    `}
`;

const StyledMenuLineIcon = styled(MenuLineIcon)`
    transform: rotate(90deg);
    color: ${props => props.theme.colors.veryDark};
`;

const Tr = styled.tr<skin.StyledProps>`
    ${props => skin.Tr()};
`;

const Th = styled.th<skin.StyledProps>`
    ${props => skin.Th()}
`;
const Td = styled.td<skin.StyledProps>`
    padding: ${props =>
        `${props.theme.layout.padding.medium} ${props.theme.layout.padding.medium}`};
`;

const PaginationStyle = styled.div`
    ${() => skin.Pagination()}
`;
const PerPageWrapper = styled.div`
    margin-right: auto;
`;
const PaginationControls = styled.div`
    display: flex;
    align-items: center;
`;
const Results = styled.div`
    ${() => skin.Td()}
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;
const ActionBar = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 0.6rem 0;
`;
const PageControlsStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.6rem;
    padding: 0.5rem 0 0.2rem 0;

    & ${Results} {
        padding: 0 ${props => props.theme.layout.padding.medium};
    }
`;
const NextPrevButton = styled.button`
    ${() => skin.NextPrevButton()}
    display: flex;
    align-items: center;

    &:hover {
        cursor: default;
        ${props =>
            !props.disabled &&
            `
        background-color: ${props.theme.colors.subtle.lightHover};
        cursor: pointer;
        `}
    }
`;

const PrevButton = styled(NextPrevButton)`
    border-radius: 0.4rem 0 0 0.4rem;
    border-right: none;
`;
const NextButton = styled(NextPrevButton)`
    border-radius: 0 0.4rem 0.4rem 0;
`;

const ThIcon = styled.th`
    cursor: pointer;
`;

const ActionButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0;
    & > button:first-child:not(:last-child) {
        border-radius: 0.4rem 0 0 0.4rem;
        border-right: none;
    }
    & > button:last-child:not(:first-child) {
        border-radius: 0 0.4rem 0.4rem 0;
    }
`;

const TableActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.8rem;
    height: 2.4rem;
    border-radius: 0.4rem;
    border: solid 0.1rem ${props => props.theme.colors.border};
    background-color: ${props => props.theme.colors.light};
    color: ${props => props.theme.colors.text.primary};
    cursor: pointer;
    padding: 0;

    &:hover {
        background-color: ${props => props.theme.colors.subtle.lightHover};
    }
`;
